import _ from 'lodash';

import {CellsConversationsDragItem} from '../../../helpers/conversationCellHelpers';
import {
  buildConversationUpdater,
  ConversationStatusesEnum,
  setConversationStatus,
  updateConversationTags,
  UpdateConversationTagsOptions,
} from '../../../helpers/models/conversationHelpers';
import {
  buildAddAndRemoveTagsConversationPatch,
  buildSetStatusConversationPatch,
} from '../../../helpers/models/conversationPatchHelpers';
import {ConversationActionContextsEnum} from '../../../models/api/conversationCommonTypes';
import {CustomViewModel, CustomViewTypesEnum} from '../../../models/api/customViewModel';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {TagModel} from '../../../models/api/tagModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {ThunkAction} from '../../appThunk';
import {ResourceMap} from '../../appTypes';
import {ConversationUndoBannerTypesEnum} from '../../panels/app/banners/bannersState';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {InboxesParameters} from '../../panels/inboxes/parameters/inboxesParameters';
import {getHasPermission} from '../access/accessSelectors';
import {updateDataAction} from '../dataGroupsActions';
import {getConversations} from '../dataSelectors';
import {getCustomViews} from '../preferences/customViewsSelectors';
import {maybeShowQuickRuleDialog} from '../rules/rulesActions';
import {getFindTeammate} from '../teammates/lazyTeammatesSelectors';
import {updateConversationsBulkAction} from './bulkConversationsActions';
import {maybeHideConversationAction} from './conversationsActions';
import {updateConversationsAction} from './updateConversationsActions';

/*
 * Simple add/remove actions.
 */

export function addTagToConversationsAction(
  conversationIds: ReadonlyArray<number>,
  tagId: number,
  tags: ReadonlyArray<TagModel>,
): ThunkAction {
  return (dispatch) => {
    // Add the tag to the store.
    dispatch(updateDataAction({tags}));

    // Update the conversation.
    return dispatch(addAndRemoveTagsFromConversationsAction(conversationIds, {add: [tagId]}));
  };
}

export function removeTagFromConversationsAction(conversationIds: ReadonlyArray<number>, tagId: number) {
  return addAndRemoveTagsFromConversationsAction(conversationIds, {remove: [tagId]});
}

export function addAndRemoveTagsFromConversationsAction(
  conversationIds: ReadonlyArray<number>,
  options: UpdateConversationTagsOptions,
  allowUndo?: boolean,
): ThunkAction {
  return (dispatch, getState) => {
    const conversationUpdater = updateConversationTags(options);

    if (conversationIds.length === 1) {
      const existingConversation = getConversations(getState())[conversationIds[0]];
      const newConversation = existingConversation && conversationUpdater(existingConversation);

      if (existingConversation && newConversation) {
        dispatch(maybeHideConversationAction(existingConversation, newConversation));
      }
    }

    const updateOptions = {
      undo: allowUndo
        ? {
            type: ConversationUndoBannerTypesEnum.TAG,
          }
        : undefined,
    };

    dispatch(
      updateConversationsAction(
        conversationIds,
        buildAddAndRemoveTagsConversationPatch(options),
        conversationUpdater,
        updateOptions,
      ),
    );
  };
}

/*
 * Simple add/remove actions.
 */

export function bulkAddTagToConversationsAction(
  inboxesParameters: InboxesParameters,
  cellsContext: CellsContext,
  tagId: number,
  tags: ReadonlyArray<TagModel>,
): ThunkAction {
  return (dispatch) => {
    // Add the tag to the store.
    dispatch(updateDataAction({tags}));

    // Update the conversation.
    return dispatch(
      bulkAddAndRemoveTagsFromConversationsAction(inboxesParameters, cellsContext, {add: [tagId]}),
    );
  };
}

export function bulkRemoveTagFromConversationsAction(
  inboxesParameters: InboxesParameters,
  cellsContext: CellsContext,
  tagId: number,
) {
  return bulkAddAndRemoveTagsFromConversationsAction(inboxesParameters, cellsContext, {remove: [tagId]});
}

export function bulkAddAndRemoveTagsFromConversationsAction(
  inboxesParameters: InboxesParameters,
  cellsContext: CellsContext,
  options: UpdateConversationTagsOptions,
): ThunkAction {
  const conversationPatch = buildAddAndRemoveTagsConversationPatch(options);
  return updateConversationsBulkAction(inboxesParameters, cellsContext, conversationPatch);
}

/*
 * Drag & Drop actions.
 */

export function canMoveToTagAction(
  options: CellsConversationsDragItem,
  tagId: number,
  namespace: string,
): ThunkAction<boolean> {
  return (_dispatch, getState) => {
    const state = getState();
    const findTeammate = getFindTeammate(state);
    const customViews = getCustomViews(state);
    const {conversationIds, sourceCustomViewId, cellsContext} = options;

    // Only allow dropping tags in the same namespace.
    const teammate = findTeammate(cellsContext.teammateId, 'canMoveToTagAction');
    if (!teammate || namespace !== teammate.namespace) {
      return false;
    }

    // Prevent dropping from public custom views.
    // TEMPORARY: This will not be needed when public custom views move to the team workspace.
    const sourceCustomView = sourceCustomViewId && customViews[sourceCustomViewId];
    if (sourceCustomView && sourceCustomView.type !== CustomViewTypesEnum.PRIVATE_TAG) {
      return false;
    }

    // Prevent dropping on the same tag.
    const originTagId = (sourceCustomView && sourceCustomView.id) || cellsContext.teammateInboxTagId;
    if (originTagId === tagId) {
      return false;
    }

    // All conversations must be in a teammate or assign context.
    if (
      conversationIds.some(
        (id) => options.findConversationActionContext(id) === ConversationActionContextsEnum.TEAM,
      )
    ) {
      return false;
    }

    // Check that the teammate has permission on all conversations.
    const hasPermission = getHasPermission(state);
    return _(conversationIds)
      .map((id) => options.findConversationNamespace(id))
      .compact() // We should have a namespace for every conversation, but in case we don't, let the action through.
      .uniq()
      .every((n) => hasPermission(n, PermissionsEnum.APPLY_TAG));
  };
}

export function moveToTagAction(
  options: CellsConversationsDragItem,
  tagId: number,
  preventUndo?: boolean,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const findTeammate = getFindTeammate(state);
    const customViews = getCustomViews(state);
    const {conversationIds, cellsContext} = options;
    const teammate = findTeammate(cellsContext.teammateId, 'moveToTagAction');

    if (!teammate) {
      return undefined;
    }

    // Track action.
    trackInterfaceEvent(InterfaceEventsEnum.DRAG_AND_DROP_TO_PRIVATE_TAG, {
      conversations: conversationIds.length === 0 ? 'single' : 'many',
      tagId,
    });

    // Find the originating tag to remove.
    const addRemoveOptions = {add: [tagId], remove: [findTagIdToRemove(options, teammate, customViews)]};

    // Undo.
    const updateOptions = {
      undo: preventUndo
        ? undefined
        : {
            type: ConversationUndoBannerTypesEnum.TAG,
          },
    };

    dispatch(maybeShowQuickRuleDialog(conversationIds, tagId));

    // Update the conversations.
    return dispatch(
      updateConversationsAction(
        conversationIds,
        (conversationId) => {
          const conversationActionContext = options.findConversationActionContext(conversationId);
          return (
            conversationActionContext && {
              // Update the tags.
              ...buildAddAndRemoveTagsConversationPatch(addRemoveOptions),
              // Re-open the conversation.
              ...buildSetStatusConversationPatch(
                cellsContext.teammateId,
                conversationActionContext,
                ConversationStatusesEnum.OPEN,
              ),
            }
          );
        },
        buildConversationUpdater(
          // Update the tags.
          updateConversationTags(addRemoveOptions),
          // Re-open the conversation.
          setConversationStatus(cellsContext, ConversationStatusesEnum.OPEN),
        ),
        updateOptions,
      ),
    );
  };
}

function findTagIdToRemove(
  options: CellsConversationsDragItem,
  currentTeammate: TeammateModel,
  customViews: ResourceMap<CustomViewModel>,
) {
  // Find the source custom view id.
  const {sourceCustomViewId} = options;

  // If the id is the same id as the starred tag, remove that instead.
  if (sourceCustomViewId && sourceCustomViewId === currentTeammate.starredTagId) {
    return currentTeammate.starredTagId;
  }

  // Otherwise try to find a custom view, grab its id or return the inbox tag id.
  return (sourceCustomViewId && customViews[sourceCustomViewId]?.id) || currentTeammate.inboxTagId;
}
